import React, { useEffect, useMemo, useRef } from 'react';
import { CustomComponentErrorBoundary } from './CustomComponentErrorBoundary';
import { useCustomComponentContext } from './CustomComponentProvider';
import type { LoadedCustomComponentModule } from './hostRuntime';
import { useCustomComponentModule } from './useCustomComponentModule';

/**
 * Reserved keys on a custom-component node's `data`. Everything else on `data` is forwarded to the
 * component as `props`.
 */
const RESERVED_DATA_KEYS = new Set(['type', 'customComponentId', 'customComponentSourceHash', 'className']);

interface CustomComponentEditorWrapperProps {
  ref?: React.Ref<HTMLDivElement>;
  editorClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDoubleClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  'data-dnd-item'?: string;
}

interface CustomComponentProps {
  customComponentId: string;
  customComponentSourceHash: string;
  className?: string;
  authorProps: Record<string, unknown>;
  errorFallback?: React.ReactNode;
  editorWrapperProps?: CustomComponentEditorWrapperProps;
}

export function splitCustomComponentData(data: Record<string, unknown>): {
  customComponentId: string;
  customComponentSourceHash: string;
  className: string | undefined;
  authorProps: Record<string, unknown>;
} {
  const id = data.customComponentId;
  if (typeof id !== 'string' || id.length === 0) {
    throw new Error('custom-component node is missing a customComponentId');
  }
  // Empty string is the Insert-menu placeholder shape — the renderer's "missing" state surfaces a
  // clear placeholder until the user picks a slug whose compile output replaces this stub.
  const sourceHash = typeof data.customComponentSourceHash === 'string' ? data.customComponentSourceHash : '';
  const className = typeof data.className === 'string' ? data.className : undefined;
  const authorProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!RESERVED_DATA_KEYS.has(key)) authorProps[key] = value;
  }
  return { customComponentId: id, customComponentSourceHash: sourceHash, className, authorProps };
}

export function CustomComponent({
  customComponentId,
  customComponentSourceHash,
  className,
  authorProps,
  errorFallback,
  editorWrapperProps,
}: CustomComponentProps): React.ReactElement {
  const ctx = useCustomComponentContext();
  const state = useCustomComponentModule(customComponentId, customComponentSourceHash);
  const isEditor = ctx?.isEditor ?? false;
  const propsSchema = ctx?.filesystem?.artifacts?.[customComponentSourceHash]?.propsSchema ?? null;

  const runtime = useMemo<RuntimeProps>(
    () => ({
      props: authorProps,
      theme: ctx?.theme,
      overrideVariables: ctx?.overrideVariables,
      isEditor,
    }),
    [authorProps, ctx?.theme, ctx?.overrideVariables, isEditor],
  );

  // Native listener (not React's onPointerDown) so it fires before dnd-kit's native handler on the
  // parent and stops parent drag from hijacking inner gestures (sliders, range inputs).
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isEditor) return undefined;
    const el = wrapperRef.current;
    if (!el) return undefined;
    const stop = (e: Event) => {
      e.stopPropagation();
    };
    el.addEventListener('pointerdown', stop);
    el.addEventListener('mousedown', stop);
    el.addEventListener('touchstart', stop);
    return () => {
      el.removeEventListener('pointerdown', stop);
      el.removeEventListener('mousedown', stop);
      el.removeEventListener('touchstart', stop);
    };
  }, [isEditor]);

  // Inline ref-merge: internal wrapperRef + editor's incoming ref both need this element.
  const setWrapperRef = (node: HTMLDivElement | null) => {
    wrapperRef.current = node;
    const incoming = editorWrapperProps?.ref;
    if (typeof incoming === 'function') {
      incoming(node);
    } else if (incoming && 'current' in incoming) {
      (incoming as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  };

  const mergedClassName = [className, editorWrapperProps?.editorClassName].filter(Boolean).join(' ') || undefined;

  return (
    <div
      ref={setWrapperRef}
      className={mergedClassName}
      data-custom-component-slug={customComponentId}
      data-custom-component-source-hash={customComponentSourceHash}
      data-dnd-item={editorWrapperProps?.['data-dnd-item']}
      onClick={editorWrapperProps?.onClick}
      onDoubleClick={editorWrapperProps?.onDoubleClick}
      onMouseEnter={editorWrapperProps?.onMouseEnter}
      onMouseLeave={editorWrapperProps?.onMouseLeave}
    >
      <CustomComponentErrorBoundary
        slug={customComponentId}
        sourceHash={customComponentSourceHash}
        isEditor={isEditor}
        onRenderError={ctx?.onRenderError ?? null}
        fallback={errorFallback}
      >
        <CustomComponentBody state={state} runtime={runtime} propsSchema={propsSchema} />
      </CustomComponentErrorBoundary>
    </div>
  );
}

interface RuntimeProps {
  props: Record<string, unknown>;
  theme: unknown;
  overrideVariables: Record<string, string> | undefined;
  isEditor: boolean;
}

interface BodyProps {
  state: ReturnType<typeof useCustomComponentModule>;
  runtime: RuntimeProps;
  propsSchema: Record<string, unknown> | null;
}

function applyJsonSchemaDefaults(
  schema: Record<string, unknown> | null | undefined,
  props: Record<string, unknown>,
): Record<string, unknown> {
  if (!schema || schema.type !== 'object') return props;
  const { properties } = schema;
  if (!properties || typeof properties !== 'object') return props;

  const result: Record<string, unknown> = { ...props };
  for (const [key, fieldRaw] of Object.entries(properties as Record<string, unknown>)) {
    if (result[key] === undefined && fieldRaw && typeof fieldRaw === 'object') {
      const field = fieldRaw as { default?: unknown };
      if ('default' in field) result[key] = field.default;
    }
  }
  return result;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '';
  }
}

function CustomComponentBody({ state, runtime, propsSchema }: BodyProps): React.ReactElement | null {
  switch (state.status) {
    case 'idle':
    case 'loading':
      return null;
    case 'no-provider':
      return (
        <div
          data-custom-component-no-provider=""
          style={{
            padding: '0.5rem 0.75rem',
            color: '#9ca3af',
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.75rem',
          }}
        >
          Custom components require a CustomComponentProvider in the page tree.
        </div>
      );
    case 'error':
      throw state.error;
    case 'ready':
      return renderModule(state.module, runtime, propsSchema, state.slug);
    default: {
      const exhaustive: never = state;
      throw new Error(`Unhandled custom component state: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function renderModule(
  module: LoadedCustomComponentModule,
  runtime: RuntimeProps,
  propsSchema: Record<string, unknown> | null,
  slug: string,
): React.ReactElement {
  const ComponentDefault = module.default;
  // Accept plain functions plus React's exotic objects (memo, forwardRef, lazy) — those have
  // `typeof === 'object'` with a `$$typeof` symbol that React's renderer recognizes.
  const isRenderableComponent =
    typeof ComponentDefault === 'function' ||
    (typeof ComponentDefault === 'object' && ComponentDefault !== null && '$$typeof' in ComponentDefault);
  if (!isRenderableComponent) {
    throw new Error(`Custom component "${slug}" does not export a default React component.`);
  }
  const Component = ComponentDefault as React.ComponentType<RuntimeProps>;
  const parsedProps = applyJsonSchemaDefaults(propsSchema, runtime.props);
  // Editor-only content key: forces remount on authored prop changes so `useState(props.x)` resets.
  const componentKey = runtime.isEditor ? safeStringify(parsedProps) : undefined;
  return <Component key={componentKey} {...runtime} props={parsedProps} />;
}
